// src/pages/settings/ProfileSettings.js
import React, { useMemo, useState } from "react";
import { Button, Collapse, Input, Upload } from "antd";
import { FaUpload } from "react-icons/fa";
import axios from "../../services/axiosConfig";
import API_ENDPOINTS from "../../services/apiEndpoints";
import { getAuthToken } from "../../auth/auth";
import SettingsRow from "./components/settingsRow";
import MultiInputField from "./components/multiInputField";

const { Panel } = Collapse;

const ProfileSettings = ({ data, onFieldChange, showNotification }) => {
  const [visible, setVisible] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // ── Logo upload ─────────────────────────────────────────────────────────────
  // Flow:
  //   1. Admin selects a file
  //   2. beforeUpload validates type and size locally
  //   3. Calls POST /settings/profile/logo/presigned to get { uploadURL, logoUrl }
  //   4. PUTs the file directly to S3 using uploadURL
  //   5. Stores the final logoUrl string in state via onFieldChange
  //   6. PATCH /settings/profile (on Save) sends logoUrl as a plain string
  const handleLogoUpload = async (file) => {
    // ── Local validation ─────────────────────────────────────────────────
    if (!file.type.startsWith("image/")) {
      showNotification("You can only upload image files!", "error", "Upload Error");
      return false;
    }
    if (file.size / 1024 / 1024 >= 5) {
      showNotification("Image must be smaller than 5MB!", "error", "Upload Error");
      return false;
    }

    // Extract extension from the file name
    const extension = file.name.split(".").pop();
    if (!extension) {
      showNotification("Could not determine file type.", "error", "Upload Error");
      return false;
    }

    setIsUploading(true);

    try {
      // ── Step 1: Get presigned URL from backend ───────────────────────
      const token = await getAuthToken();
      const response = await axios.post(
        API_ENDPOINTS.SETTINGS_PROFILE_LOGO_PRESIGNED,
        { extension, mimeType: file.type },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { uploadURL, logoUrl } = response.data;

      // ── Step 2: Upload directly to S3 ───────────────────────────────
      await fetch(uploadURL, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type }
      });

      // ── Step 3: Store the final S3 URL in state ──────────────────────
      // logoUrl is a plain string — ready to be saved in PATCH /settings/profile
      onFieldChange("churchLogo", logoUrl);

      showNotification("Logo uploaded successfully.", "success", "Upload Complete");
    } catch (error) {
      console.error("Logo upload failed:", error);
      showNotification("Failed to upload logo. Please try again.", "error", "Upload Error");
    } finally {
      setIsUploading(false);
    }

    // Always return false — we handle the upload manually, not via antd
    return false;
  };

  const uploadProps = useMemo(
    () => ({
      beforeUpload: handleLogoUpload,
      fileList: data.churchLogo
        ? [
            {
              uid: "-1",
              name: "logo",
              status: isUploading ? "uploading" : "done",
              url: data.churchLogo
            }
          ]
        : [],
      onChange: ({ fileList }) => {
        // If the admin removes the logo from the file list, clear it from state
        if (fileList.length === 0) onFieldChange("churchLogo", null);
      },
      maxCount: 1,
      accept: "image/*"
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.churchLogo, isUploading]
  );

  return (
    <div className="stg-sub-page">
      <Collapse className="stg-collapse" defaultActiveKey={["1"]}>
        <Panel header="Main Profile Details" key="1">
          <SettingsRow label="Church Unique Code">
            <Input.Password value={data.churchCode} readOnly visibilityToggle={{ visible, onVisibleChange: setVisible }} />
          </SettingsRow>
          <SettingsRow label="Church Name">
            <Input placeholder="Enter church name" value={data.churchName} onChange={(e) => onFieldChange("churchName", e.target.value)} />
          </SettingsRow>
          <SettingsRow label="Church Logo">
            <Upload {...uploadProps} listType="picture">
              <Button icon={<FaUpload />} loading={isUploading}>
                {isUploading ? "Uploading..." : "Upload Image"}
              </Button>
            </Upload>
          </SettingsRow>
        </Panel>

        <Panel header="About Us Details" key="2">
          <SettingsRow label="Church Description">
            <Input.TextArea
              rows={3}
              placeholder="Enter description"
              value={data.description}
              onChange={(e) => onFieldChange("description", e.target.value)}
            />
          </SettingsRow>
          <SettingsRow label="Church Website">
            <Input placeholder="https://example.com" value={data.website} onChange={(e) => onFieldChange("website", e.target.value)} />
          </SettingsRow>
          <MultiInputField
            label="Social Media Link"
            placeholder="https://facebook.com/..."
            value={data.socialMedia}
            onChange={(v) => onFieldChange("socialMedia", v)}
          />
        </Panel>

        <Panel header="Contact Information" key="3">
          <SettingsRow label="Address">
            <Input.TextArea
              rows={2}
              placeholder="Enter address"
              value={data.address}
              onChange={(e) => onFieldChange("address", e.target.value)}
            />
          </SettingsRow>
          <SettingsRow label="Address Link (Maps)">
            <Input placeholder="Google Maps URL" value={data.addressLink} onChange={(e) => onFieldChange("addressLink", e.target.value)} />
          </SettingsRow>
          <MultiInputField
            label="Phone Number"
            placeholder="+1 234 567 8900"
            value={data.phoneNumbers}
            onChange={(v) => onFieldChange("phoneNumbers", v)}
          />
          <MultiInputField
            label="Email Address"
            placeholder="contact@church.com"
            value={data.emails}
            onChange={(v) => onFieldChange("emails", v)}
          />
          <MultiInputField
            label="Operating Hours"
            placeholder="Monday: 9AM - 5PM"
            value={data.operatingHours}
            onChange={(v) => onFieldChange("operatingHours", v)}
          />
        </Panel>
      </Collapse>
    </div>
  );
};

export default ProfileSettings;
